// The 9 chiropractic note types. This catalog is the frontend source of truth
// so the shell works in dev without waiting on backend note-type provisioning.
// `value` matches the backend note-type key (doctorTypes:["chiropractor"]) once
// those templates exist; until then the chiro app reads these directly.
//
// accent: "clinical" (indigo) | "legal" (amber). icon: a name from widgets/Icon.

export const NOTE_TYPES = [
  {
    value: "chiro-intake",
    num: "01",
    label: "Intake Exam",
    icon: "intake",
    accent: "clinical",
    desc: "New-patient deep dive: mechanism, history, per-region symptoms, exam, screening tools. The baseline everything else references.",
    flow: { text: "feeds all notes", kind: "default" },
    newEncounter: true,
  },
  {
    value: "chiro-fu",
    num: "02",
    label: "Follow-Up Note",
    icon: "check",
    accent: "clinical",
    desc: "Carry-forward summary on top, today's complaints below, 4-region body diagram, and documented findings per region.",
    flow: { text: "pulls from intake", kind: "default" },
    newEncounter: true,
  },
  {
    value: "chiro-pt",
    num: "07",
    label: "Physical Therapy",
    icon: "activity",
    accent: "clinical",
    desc: "New eval & F/U: objective measures, prescribed exercises, modalities, tolerance, and functional progress.",
    flow: { text: "new note type", kind: "new" },
    newEncounter: true,
  },
  {
    value: "chiro-proc",
    num: "08",
    label: "Procedure Note",
    icon: "edit",
    accent: "clinical",
    desc: "Discrete procedures: technique, region, consent, response, post-care, coded and time-stamped.",
    flow: { text: "Beta", kind: "beta" },
    newEncounter: true,
  },
  {
    value: "chiro-interim",
    num: "03",
    label: "Interim Summary",
    icon: "chart",
    accent: "clinical",
    desc: "1-click: pick chart range, set tone, dictate your opinion. Rapid care, legal & workman's-comp updates.",
    flow: { text: "1-click generate", kind: "default" },
    newEncounter: true,
  },
  {
    value: "chiro-full",
    num: "04",
    label: "Full Summary",
    icon: "file",
    accent: "clinical",
    desc: "The complete episode: total visits, overall progress, all studies & referrals, MMI, prognosis.",
    flow: { text: "whole course", kind: "default" },
    newEncounter: true,
  },
  {
    value: "chiro-legal",
    num: "05",
    label: "Med/Legal Update",
    icon: "shield",
    accent: "legal",
    desc: "Interim summary with rigid rules: scans for case-harming language and won't let a note prematurely close care.",
    flow: { text: "Coming Soon", kind: "soon" },
    newEncounter: true,
  },
  {
    value: "chiro-auth",
    num: "06",
    label: "Prior Authorization",
    icon: "authdoc",
    accent: "legal",
    desc: "Pre-filled from the chart: MRI/CT, DME, specialist referral, extended care. One tap, ready to fax.",
    flow: { text: "Coming Soon", kind: "soon" },
    newEncounter: true,
  },
  {
    value: "chiro-referral",
    num: "09",
    label: "Referral Note",
    icon: "referral",
    accent: "legal",
    desc: "To ortho, pain management, surgery, or imaging, with the clinical justification pulled straight from the record.",
    flow: { text: "Coming Soon", kind: "soon" },
    newEncounter: true,
  },
];

export const NOTE_TYPE_MAP = NOTE_TYPES.reduce((m, n) => {
  m[n.value] = n;
  return m;
}, {});

// Left-rail note-organizer groups (image 8). `system` items are app pages, not
// note types, so they carry an explicit `view` instead of a note `value`.
export const NAV_GROUPS = [
  {
    labelKey: "nav.workspace",
    items: [
      { view: "dashboard", label: "Dashboard", icon: "dashboard" },
      // Scheduler surface. (Re-add `preview: "chiro-scheduler"` here to hide it
      // behind ?devPreview=chiro-scheduler before this ships to all chiro users.)
      { view: "schedule", label: "Schedule", labelKey: "nav.schedule", icon: "calendar" },
    ],
  },
  {
    labelKey: "nav.examsVisits",
    items: ["chiro-intake", "chiro-fu", "chiro-pt", "chiro-proc"],
  },
  {
    labelKey: "nav.summaries",
    items: ["chiro-interim", "chiro-full"],
  },
  {
    labelKey: "nav.medicalLegal",
    items: ["chiro-legal", "chiro-auth", "chiro-referral"],
  },
];

// The most-common types surfaced for quick start. Lightning on the rail uses the
// last-used type, falling back to the first of these.
export const COMMON_NOTE_TYPES = ["chiro-intake", "chiro-fu", "chiro-interim"];
